import React from 'react';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import { IFinalCalloutFields } from '../../../types/contentful';

import { Box } from '../Box';
import { BlockContainer, Layout, Column } from '../Layout';
import { Text } from '../Text';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import CtaWidget from '../CtaWidget';

import {
  ctaWidgetEnum,
  DEFAULT_LABEL_V1,
  DEFAULT_PLACEHOLDER_V1
} from '../CtaWidget/shared';

import { InlineAnchor } from '../RichText/styles';
import { DisclosureParagraph, CtaWrapper, PrivacyWrapper } from './styles';

const DEFAULT_HEADLINE = 'Try today. Our treat.';
const DEFAULT_SUBHEADLINE =
  'After your one month trial, plans start at just $4.99/month for the whole family. Includes up to five kids.';

export function FinalCallout({
  headerText = DEFAULT_HEADLINE,
  subheaderText = DEFAULT_SUBHEADLINE,
  useLightText = false,
  backgroundColor,
  registrationEntrypoint,
  privacyPolicy
}: IFinalCalloutFields) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
      <DisclosureParagraph colour={useLightText ? 'white' : 'defaultText'}>
        {children}
      </DisclosureParagraph>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineAnchor
        href={node.data.uri}
        colour={useLightText ? 'white' : 'defaultText'}
      >
        {children}
      </InlineAnchor>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour={useLightText ? 'white' : 'defaultText'}
        fontWeight="p"
      >
        {children}
      </EmbeddedInlineEntryNode>
    )
  };

  return (
    <BlockContainer
      as="section"
      marginTop={{
        0: 'blockMarginY',
        tablet: 'blockMarginY_tablet'
      }}
      marginBottom={{
        0: 'blockMarginY',
        tablet: 'blockMarginY_tablet'
      }}
      data-blockid="final-cta"
      data-testid="final-callout"
    >
      <Layout
        hexOverrideBgColor={backgroundColor ?? '80D6B0'}
        borderRadius={{ 0: 'block', desktop: 'block_desktop' }}
        paddingTop={{ 0: 'l', tablet: 'xl' }}
        paddingBottom={{ 0: 'l', tablet: 'xl' }}
      >
        <Column
          size={{ 0: 10, tablet: 8, desktop: 6, max: 5 }}
          flexDirection="column"
        >
          <Text
            as="h2"
            fontWeight="h2"
            fontSize={{ 0: 'h2_0', tablet: 'h2_desktop' }}
            colour={useLightText ? 'white' : 'defaultText'}
            textAlign="center"
            style={{ letterSpacing: '.5px' }}
          >
            {headerText}
          </Text>
          <Box marginTop={{ 0: 'sm', tablet: 'm' }}>
            <Text
              as="p"
              colour={useLightText ? 'white' : 'defaultText'}
              textAlign="center"
              fontSize={{ 0: 'p_0', tablet: 'p_desktop' }}
            >
              {subheaderText}
            </Text>
          </Box>
          <CtaWrapper
            flexDirection="column"
            justifyContent="center"
            justifySelf="center"
            alignItems="center"
            margin={{
              0: ['m', 'auto', 0, 'auto'],
              tablet: ['ml', 'auto', 0, 'auto']
            }}
          >
            <CtaWidget
              {...registrationEntrypoint?.fields}
              id="footer-phone-number"
              module="Final Callout"
              errorPosition="bottom"
              designType={ctaWidgetEnum.DESKTOP_PHONE_NUMBER_V1}
              placeholderText={DEFAULT_PLACEHOLDER_V1}
              labelText={DEFAULT_LABEL_V1}
              sysId={
                registrationEntrypoint?.sys
                  ? registrationEntrypoint?.sys?.contentType?.sys?.id
                  : 'nonContentfulCta'
              }
            />
          </CtaWrapper>
          {privacyPolicy && (
            <PrivacyWrapper>
              <RichText
                colour="footerText"
                data={privacyPolicy}
                overrideOptions={RICH_TEXT_OVERRIDE}
              />
            </PrivacyWrapper>
          )}
        </Column>
      </Layout>
    </BlockContainer>
  );
}
